import { apiFetch } from './apiClient.js';

/**
 * #150/#151 — Cliente HTTP del CRUD CcConnector (Five9 Digital per-tenant).
 *
 * Five9 Digital usa auth ANÓNIMO cookieless: no hay credencial estática que
 * configurar. El admin sólo carga metadata (URLs, tenantName, campaign).
 * El runtime se autentica dinámicamente contra `authBaseUrl` con
 * `{ tenantName }`.
 */

export type CcProvider = 'five9_digital';
export type CcConnectorStatus = 'connected' | 'error';

export interface CcConnector {
  id: string;
  tenantId: string;
  name: string;
  provider: CcProvider;
  /** URL del endpoint de auth anon Five9. */
  authBaseUrl: string;
  /** Opcional: URL del API de messages/interactions. Fallback a apiHost del auth. */
  messagesBaseUrl?: string;
  /** Nombre del tenant en Five9 (no nuestro tenantId). Va al body del auth. */
  tenantName: string;
  campaign: string;
  interactionTaskKey: string;
  status: CcConnectorStatus;
  version: number;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface CcConnectorCreate {
  name: string;
  provider: CcProvider;
  authBaseUrl: string;
  messagesBaseUrl?: string;
  tenantName: string;
  campaign: string;
  interactionTaskKey: string;
}

export interface CcConnectorPatch {
  name?: string;
  authBaseUrl?: string;
  messagesBaseUrl?: string;
  tenantName?: string;
  campaign?: string;
  interactionTaskKey?: string;
  status?: CcConnectorStatus;
  /** Optimistic concurrency. */
  expectedVersion: number;
}

interface ListCcConnectorsResponse {
  items: CcConnector[];
}

export async function listCcConnectors(
  tenantId: string,
  opts: { includeArchived?: boolean } = {},
): Promise<CcConnector[]> {
  const qs = opts.includeArchived ? '?includeArchived=true' : '';
  const res = await apiFetch<ListCcConnectorsResponse | CcConnector[]>(
    `/v1/tenants/${encodeURIComponent(tenantId)}/cc-connectors${qs}`,
  );
  return Array.isArray(res) ? res : res.items;
}

export async function getCcConnector(
  tenantId: string,
  connectorId: string,
): Promise<CcConnector> {
  return apiFetch<CcConnector>(
    `/v1/tenants/${encodeURIComponent(tenantId)}/cc-connectors/${encodeURIComponent(connectorId)}`,
  );
}

export async function createCcConnector(
  tenantId: string,
  body: CcConnectorCreate,
): Promise<CcConnector> {
  return apiFetch<CcConnector>(
    `/v1/tenants/${encodeURIComponent(tenantId)}/cc-connectors`,
    { method: 'POST', body },
  );
}

export async function updateCcConnector(
  tenantId: string,
  connectorId: string,
  patch: CcConnectorPatch,
): Promise<CcConnector> {
  return apiFetch<CcConnector>(
    `/v1/tenants/${encodeURIComponent(tenantId)}/cc-connectors/${encodeURIComponent(connectorId)}`,
    {
      method: 'PATCH',
      body: patch,
      headers: { 'If-Match': String(patch.expectedVersion) },
    },
  );
}

export async function archiveCcConnector(
  tenantId: string,
  connectorId: string,
  expectedVersion: number,
): Promise<void> {
  await apiFetch<void>(
    `/v1/tenants/${encodeURIComponent(tenantId)}/cc-connectors/${encodeURIComponent(connectorId)}/archive`,
    {
      method: 'POST',
      headers: { 'If-Match': String(expectedVersion) },
    },
  );
}
